import React, { useEffect, useMemo, useState } from 'react';
import { Card, Form, Input, Button, Typography, Space, Descriptions, Avatar, message, Upload } from 'antd';
import { SaveOutlined, ReloadOutlined, GlobalOutlined, IdcardOutlined, PictureOutlined, UserOutlined, MailOutlined, LockOutlined, UploadOutlined } from '@ant-design/icons';
import api from '../../../utils/api';
import { useAuth } from '../../../contexts/AuthContext';

const { Title, Paragraph, Text } = Typography;

const BusinessProfile = () => {
  const { currentUser, updateUser, changePassword } = useAuth();
  const [form] = Form.useForm();
  const [accountForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updatingAccount, setUpdatingAccount] = useState(false);
  const [changingPwd, setChangingPwd] = useState(false);
  const [record, setRecord] = useState(null); // existing whitelabel_agencies row
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);

  const ownerUserId = currentUser?.id;

  const reload = async () => {
    if (!ownerUserId) return;
    setLoading(true);
    try {
      const data = await api.get(`/whitelabel-agencies/owner/${ownerUserId}`);
      setRecord(data);
      form.setFieldsValue({
        name: data.name,
        domain: data.domain || '',
        logo_url: data.logo_url || ''
      });
    } catch (err) {
      // If 404, stay in create mode.
      console.debug('No existing whitelabel agency for owner; create mode. Error (if any):', err?.message);
    } finally {
      setLoading(false);
    }
  };

  // Account avatar upload
  const fileToDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const accountAvatarUploadProps = {
    name: 'file',
    accept: 'image/*',
    showUploadList: false,
    maxCount: 1,
    customRequest: async ({ file, onSuccess, onError }) => {
      try {
        if (!file || !(file).type?.startsWith('image/')) {
          message.error('Please select a valid image file');
          return onError?.(new Error('Invalid file'));
        }
        if (!currentUser?.id) return onError?.(new Error('No user'));
        setUploadingAvatar(true);
        const dataUrl = await fileToDataUrl(file);
        const updated = await api.uploadUserProfilePicture(currentUser.id, { dataUrl });
        setAvatarUrl(updated.profile_picture_url || null);
        updateUser({ profile_picture_url: updated.profile_picture_url || null });
        message.success('Profile picture updated');
        onSuccess?.(updated);
      } catch (err) {
        console.error(err);
        message.error(err.message || 'Failed to upload picture');
        onError?.(err);
      } finally {
        setUploadingAvatar(false);
      }
    },
  };

  const onUpdateAccount = async () => {
    if (!currentUser?.id) return message.error('Missing user');
    try {
      const values = await accountForm.validateFields();
      setUpdatingAccount(true);
      const payload = {
        full_name: values.full_name,
        email: values.email
      };
      const updated = await api.put(`/users/${currentUser.id}`, payload);
      updateUser({ full_name: updated.full_name, email: updated.email });
      message.success('Account updated');
    } catch (err) {
      if (err?.errorFields) return; // validation error
      message.error(err.message || 'Failed to update account');
    } finally {
      setUpdatingAccount(false);
    }
  };

  const onChangePassword = async () => {
    if (!currentUser?.id) return message.error('Missing user');
    try {
      const { current_password, new_password, confirm_password } = await passwordForm.validateFields();
      if (new_password !== confirm_password) {
        return message.error('New password and confirmation do not match');
      }
      setChangingPwd(true);
      await changePassword(current_password, new_password);
      passwordForm.resetFields();
    } catch (err) {
      if (err?.errorFields) return; // validation error
      message.error(err.message || 'Failed to change password');
    } finally {
      setChangingPwd(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerUserId]);

  // init avatar preview from auth user
  useEffect(() => {
    setAvatarUrl(currentUser?.profile_picture_url || null);
  }, [currentUser]);

  // Prefill account form from current user
  useEffect(() => {
    if (currentUser) {
      accountForm.setFieldsValue({
        full_name: currentUser.full_name || '',
        email: currentUser.email || ''
      });
    }
  }, [currentUser, accountForm]);

  const onReset = () => {
    if (record) {
      form.setFieldsValue({ name: record.name, domain: record.domain || '', logo_url: record.logo_url || '' });
    } else {
      form.resetFields();
    }
  };


  const uploadProps = {
    name: 'file',
    accept: 'image/*',
    showUploadList: false,
    maxCount: 1,
    customRequest: async ({ file, onSuccess, onError }) => {
      try {
        if (!record?.id) {
          message.warning('Create the business profile first');
          return onError?.(new Error('No record'));
        }
        if (!file || !(file).type?.startsWith('image/')) {
          message.error('Please select a valid image file');
          return onError?.(new Error('Invalid file'));
        }
        setUploadingLogo(true);
        const dataUrl = await fileToDataUrl(file);
        const updated = await api.uploadWhitelabelLogo(record.id, { dataUrl });
        setRecord(updated);
        form.setFieldsValue({ logo_url: updated.logo_url || '' });
        message.success('Logo updated');
        onSuccess?.(updated);
      } catch (err) {
        console.error(err);
        message.error(err.message || 'Failed to upload logo');
        onError?.(err);
      } finally {
        setUploadingLogo(false);
      }
    },
  };

  const onFinish = async (values) => {
    if (!ownerUserId) return message.error('Missing owner user');
    setSaving(true);
    try {
      if (record?.id) {
        await api.put(`/whitelabel-agencies/${record.id}`, {
          name: values.name,
          domain: values.domain || null,
          logo_url: values.logo_url || null,
          owner_user_id: ownerUserId,
        });
        await reload();
        message.success('Business profile updated');
      } else {
        await api.post('/whitelabel-agencies', {
          name: values.name,
          domain: values.domain || null,
          logo_url: values.logo_url || null,
          owner_user_id: ownerUserId,
        });
        await reload();
        message.success('Business profile created');
      }
    } catch (err) {
      message.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const logo = useMemo(() => {
    const url = form.getFieldValue('logo_url') || record?.logo_url;
    if (!url) return null;
    return (
      <Avatar shape="square" size={64} src={url} alt="Logo" style={{ border: '1px solid #f0f0f0' }} />
    );
  }, [form, record]);

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={3}>Business Profile</Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Manage your white-label agency details. Fields map to table <Text code>whitelabel_agencies</Text>.
          </Paragraph>
        </div>

        <Card loading={loading}>
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{ name: '', domain: '', logo_url: '' }}
          >
            <Form.Item
              name="name"
              label={<span><IdcardOutlined /> Agency Name</span>}
              rules={[{ required: true, message: 'Please input the agency name' }]}
            >
              <Input placeholder="e.g., Acme Media Network" maxLength={150} allowClear />
            </Form.Item>

            <Form.Item
              name="domain"
              label={<span><GlobalOutlined /> Domain</span>}
              tooltip="Must be unique across all white-label agencies"
              rules={[{ type: 'string' }]}
            >
              <Input placeholder="e.g., acmemedia.com" maxLength={255} allowClear />
            </Form.Item>

            <Form.Item
              name="logo_url"
              label={<span><PictureOutlined /> Logo URL</span>}
              rules={[{ type: 'string' }]}
            >
              <Input placeholder="https://.../logo.png" allowClear />
            </Form.Item>
            <div style={{ marginTop: -8, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Upload {...uploadProps} disabled={!record?.id}>
                <Button icon={<UploadOutlined />} loading={uploadingLogo} disabled={!record?.id}>
                  Upload from device
                </Button>
              </Upload>
              {!record?.id && (
                <span style={{ color: '#999' }}>Create the business profile first to enable uploading</span>
              )}
            </div>

            <Space style={{ marginTop: 8 }}>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}>
                {record ? 'Save Changes' : 'Create Profile'}
              </Button>
              <Button onClick={onReset} icon={<ReloadOutlined />}>Reset</Button>
            </Space>
          </Form>
        </Card>

        <Card title="Account" size="small">
          {/* Avatar for logged-in account */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <img
              src={avatarUrl || '/icon.png'}
              alt="avatar"
              style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', border: '1px solid #f0f0f0' }}
              onError={(e) => { e.currentTarget.src = '/icon.png'; }}
            />
            <Upload {...accountAvatarUploadProps}>
              <Button icon={<UploadOutlined />} loading={uploadingAvatar}>Upload new picture</Button>
            </Upload>
          </div>
          <Form form={accountForm} layout="vertical" initialValues={{ full_name: '', email: '' }}>
            <Form.Item
              name="full_name"
              label={<span><UserOutlined /> Full Name</span>}
              rules={[{ required: true, message: 'Please input your full name' }]}
            >
              <Input placeholder="Your full name" allowClear />
            </Form.Item>
            <Form.Item
              name="email"
              label={<span><MailOutlined /> Email</span>}
              rules={[{ required: true, message: 'Please input your email' }, { type: 'email', message: 'Invalid email' }]}
            >
              <Input placeholder="you@example.com" allowClear />
            </Form.Item>
            <Space>
              <Button type="primary" icon={<SaveOutlined />} loading={updatingAccount} onClick={onUpdateAccount}>
                Update Account
              </Button>
            </Space>
          </Form>
        </Card>

        <Card title="Change Password" size="small">
          <Form form={passwordForm} layout="vertical">
            <Form.Item
              name="current_password"
              label={<span><LockOutlined /> Current Password</span>}
              rules={[{ required: true, message: 'Please input your current password' }]}
            >
              <Input.Password placeholder="Current password" />
            </Form.Item>
            <Form.Item
              name="new_password"
              label={<span><LockOutlined /> New Password</span>}
              rules={[{ required: true, message: 'Please input a new password' }, { min: 6, message: 'Minimum 6 characters' }]}
            >
              <Input.Password placeholder="New password" />
            </Form.Item>
            <Form.Item
              name="confirm_password"
              label={<span><LockOutlined /> Confirm New Password</span>}
              dependencies={["new_password"]}
              rules={[
                { required: true, message: 'Please confirm your new password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('new_password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('The two passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password placeholder="Confirm new password" />
            </Form.Item>
            <Space>
              <Button type="primary" icon={<SaveOutlined />} loading={changingPwd} onClick={onChangePassword}>
                Update Password
              </Button>
              <Button icon={<ReloadOutlined />} onClick={() => passwordForm.resetFields()}>Reset</Button>
            </Space>
          </Form>
        </Card>

        <Card title="Profile Info" size="small">
          <Descriptions bordered size="small" column={1} labelStyle={{ width: 200 }}>
            <Descriptions.Item label="Owner User ID"><Text code>{ownerUserId || '-'}</Text></Descriptions.Item>
            <Descriptions.Item label="Record ID"><Text code>{record?.id ?? '-'}</Text></Descriptions.Item>
            <Descriptions.Item label="Created At">{record?.created_at ? new Date(record.created_at).toLocaleString() : '-'}</Descriptions.Item>
            <Descriptions.Item label="Logo Preview">{logo || <Text type="secondary">No logo</Text>}</Descriptions.Item>
          </Descriptions>
        </Card>
      </Space>
    </div>
  );
};

export default BusinessProfile;
